import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2 } from "lucide-react";

const TIMES = Array.from({ length: 9 }, (_, i) => {
  const h = 18 + Math.floor(i / 2);
  return `${h}:${i % 2 === 0 ? "00" : "30"}`;
});

const inputCls = "rounded-none border-[#D8D4CC] bg-white h-12 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] text-sm";
const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#555] mb-2";

export function ReservationSection() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "20:00", guests: 2, note: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [sectionImg, setSectionImg] = useState(null);

  useEffect(() => {
    api.get("/site-images").then((r) => {
      const u = r.data?.prenotazione;
      if (u) setSectionImg(u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (form.date < today) {
      toast.error(lang === "it" ? "La data non può essere nel passato." : "The date cannot be in the past.");
      return;
    }
    setSending(true);
    try {
      await api.post("/reservations", { ...form, guests: Number(form.guests) });
      setDone(form);
    } catch (err) {
      toast.error(formatApiError(err, t.reservation.error));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="prenota" data-testid="reservation-section" className="py-28 lg:py-40 bg-[#0A0A0A] text-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <div>
          <motion.p className="text-xs uppercase tracking-[0.35em] text-[#999] mb-6"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            {t.reservation.overline}
          </motion.p>
          <motion.h2 data-testid="reservation-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight"
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1 }}>
            {t.reservation.title}
          </motion.h2>
          <motion.p className="mt-6 text-sm text-[#BBB] leading-relaxed max-w-md"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
            {t.reservation.sub}
          </motion.p>
          <motion.a
            data-testid="res-whatsapp-link"
            href="https://wa.me/393520981771"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-6 text-xs uppercase tracking-[0.25em] text-[#FAF9F6] border-b border-[#6B1E33] pb-1 hover:text-[#6B1E33] transition-colors duration-300"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.25 }}
          >
            WhatsApp · +39 352 098 1771
          </motion.a>
          <motion.div className="mt-14 hidden lg:block overflow-hidden w-80"
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }}>
            <img
              src={sectionImg || "https://images.unsplash.com/photo-1697056657100-06c2bcecefc4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwcGl6emVyaWElMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODY3MjUzMDJ8MA&ixlib=rb-4.1.0&q=85"}
              alt="La sala di Funghetto Roma" className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700" />
          </motion.div>
        </div>

        <motion.div
          className="border border-[#333] p-8 lg:p-12"
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.15 }}
        >
          {done ? (
            <div data-testid="reservation-success" className="text-center py-10">
              <CheckCircle2 className="mx-auto mb-6 text-[#6B1E33]" size={44} strokeWidth={1.5} />
              <h3 className="font-serif text-3xl mb-4">{t.reservation.successTitle}</h3>
              <p className="text-sm text-[#BBB] leading-relaxed max-w-sm mx-auto">
                {t.reservation.successText
                  .replace("{name}", done.name)
                  .replace("{guests}", done.guests)
                  .replace("{date}", done.date)
                  .replace("{time}", done.time)}
              </p>
              <button data-testid="reservation-again-btn" onClick={() => { setDone(null); setForm({ name: "", email: "", phone: "", date: "", time: "20:00", guests: 2, note: "" }); }}
                className="mt-10 text-xs uppercase tracking-[0.25em] border border-[#FAF9F6] px-8 py-4 hover:bg-[#FAF9F6] hover:text-[#0A0A0A] transition-colors duration-300">
                {t.reservation.again}
              </button>
            </div>
          ) : (
            <form data-testid="reservation-form" onSubmit={submit} className="space-y-6">
              <div>
                <label className={labelCls} htmlFor="res-name">{t.reservation.name}</label>
                <Input id="res-name" data-testid="res-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls + " text-[#0A0A0A]"} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="res-email">{t.reservation.email}</label>
                  <Input id="res-email" data-testid="res-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls + " text-[#0A0A0A]"} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="res-phone">{t.reservation.phone}</label>
                  <Input id="res-phone" data-testid="res-phone-input" type="tel" required minLength={5} value={form.phone} onChange={set("phone")} className={inputCls + " text-[#0A0A0A]"} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls} htmlFor="res-date">{t.reservation.date}</label>
                  <Input id="res-date" data-testid="res-date-input" type="date" required min={today} value={form.date} onChange={set("date")}
                    style={{ colorScheme: "light" }}
                    className="w-full h-12 border border-[#D8D4CC] bg-white text-[#0A0A0A] text-sm px-3 rounded-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] appearance-none" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="res-time">{t.reservation.time}</label>
                  <select id="res-time" data-testid="res-time-select" value={form.time} onChange={set("time")}
                    className="w-full h-12 border border-[#D8D4CC] bg-white text-[#0A0A0A] text-sm px-3 rounded-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]">
                    {TIMES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="res-guests">{t.reservation.guests}</label>
                  <select id="res-guests" data-testid="res-guests-select" value={form.guests} onChange={set("guests")}
                    className="w-full h-12 border border-[#D8D4CC] bg-white text-[#0A0A0A] text-sm px-3 rounded-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} {n === 1 ? t.reservation.guestsUnit.one : t.reservation.guestsUnit.many}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="res-note">{t.reservation.note}</label>
                <Textarea id="res-note" data-testid="res-note-input" value={form.note} onChange={set("note")} placeholder={t.reservation.notePh}
                  className="rounded-none border-[#D8D4CC] bg-white text-[#0A0A0A] text-sm min-h-[90px] focus-visible:ring-1 focus-visible:ring-[#0A0A0A]" />
              </div>
              <button type="submit" data-testid="res-submit-btn" disabled={sending}
                className="w-full text-xs uppercase tracking-[0.3em] bg-[#6B1E33] text-[#FAF9F6] py-5 hover:bg-[#FAF9F6] hover:text-[#0A0A0A] transition-colors duration-300 disabled:opacity-50">
                {sending ? t.reservation.sending : t.reservation.submit}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
